import format from 'date-fns/format';
import { useCallback, useMemo } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/assets/static/ApiRoutes';
import { UseClosePeriodPermissions } from 'src/components/guards/PermissionsList';
import useAsync from 'src/hooks/useAsync';
import usePermission from 'src/hooks/usePermissions';
import axiosInstance from 'src/utils/axios';
import { sortGLPeriods } from '../utils/transformation';

export interface GLPeriodResponseObject {
  glPeriodId: number;
  fiscalYear: number;
  year: number;
  startDate: string;
  endDate: string;
  hasTransactionData: boolean;
  rowId: string;
  isClosed: null | number;
}

export type GLPeriodResponse = {
  glPeriods: GLPeriods;
  usesNonCalenderPeriods: boolean;
};

export interface GlPeriodObject extends GLPeriodResponseObject {
  period: string;
  canClosePeriod: boolean;
}

export type GLPeriods = GlPeriodObject[];

const useGLPeriodApi = (companyId: string) => {
  const usePeriods = usePermission({ permissions: UseClosePeriodPermissions });
  const call = useCallback(
    () =>
      new Promise<GLPeriodResponse>((resolve, reject) => {
        if(companyId){
          axiosInstance
          .get<GLPeriodResponse>(api.glPeriod(companyId))
          .then((value) => {
            resolve({
              ...value.data,
              glPeriods:
                value.data?.glPeriods?.map((item) => ({
                  ...item,
                  period: item.startDate ? format(new Date(item.startDate), 'MM') : '',
                  rowId: uuid(),
                  isClosed: usePeriods && item.isClosed === 1 ? 1 : 0,
                })) || [],
            });
          })
          .catch((error) => {
            reject(error);
          });
        } else {
          reject()
        }
        
      }),
    [companyId, usePeriods]
  );

  const { isLoading, value, execute } = useAsync(call, false, undefined, {} as GLPeriodResponse);
  const glPeriods =  useMemo(() => sortGLPeriods(value?.glPeriods ?? []),[value?.glPeriods]);

  const glPeriodsObject = useMemo(() => glPeriods.reduce<Record<string,GlPeriodObject>>((acc, period) => {
    acc[period.glPeriodId.toString()] = period;
    return acc;
  }, {}),[glPeriods])

  return {
    glPeriods,
    isLoading,
    usesNonCalenderPeriods: value?.usesNonCalenderPeriods,
    getGLPeriods: execute,
    glPeriodsObject
  };
};

export default useGLPeriodApi;
